import { VISUAL_ROLES, type AvatarCharacter } from "@eldrun/content";
import type { Mesh, Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import type { AvatarLibrary } from "../render/avatar-library.ts";
import { applyPose, AvatarAnimator } from "./avatar-animation.ts";
import { createAvatarModel, type AvatarRig } from "./avatar-model.ts";
import type { AvatarSignals } from "./avatar-state.ts";
import { poseHeld } from "./held-item.ts";
import type { HeldView } from "./held-item.ts";
import { AVATAR_LAYER, SkinnedAvatar } from "./skinned-avatar.ts";
import { swingArm } from "./swing.ts";

/** O que a cena entrega a cada avatar no quadro. */
export interface AvatarFrame {
  /** s desde o quadro anterior. */
  dt: number;
  /** ms (o relógio da cena). */
  now: number;
  yaw: number;
  pitch: number;
  signals: AvatarSignals;
  /** Golpe do braço do boneco procedural (inclui o do arco, que não é gesto dos avatares animados). */
  armSwing: number;
  /** Instante da morte (ms); −1 vivo. */
  diedAt: number;
  /** Para o LOD da animação: distância da câmera (m), se está na tela, o quadro da cena e a distância de visão. */
  distance: number;
  visible: boolean;
  frame: number;
  range: number;
}

export interface AvatarViewStats {
  /** Personagem (`cavaleiro`…) ou `procedural`. */
  model: string;
  /** Clipe de maior peso da camada de baixo e o gesto por cima, se houver. */
  clip: string;
  gesture?: string;
  /** Quadros por atualização da animação (1, 2, 4; 0 = parada). */
  lod: number;
  /** Dissolve atual (0 inteiro, 1 sumido). */
  dissolve: number;
  /** Quantas vezes entrou em cada estado do corpo e gesto (`morte`, `golpe`…). */
  seen: Partial<Record<string, number>>;
}

/**
 * Um jogador na cena (F49): o avatar animado (`SkinnedAvatar`) ou, sem o pacote, o boneco procedural
 * de antes (ADR 0007). A cena põe `root` no lugar e na direção; o resto (pose, morte, item na mão)
 * passa por aqui.
 */
export interface AvatarView {
  readonly root: Object3D;
  /** Como o item na mão se prende (`heldMesh`). */
  readonly heldView: HeldView;
  update(frame: AvatarFrame): void;
  /** Dissolve (F21): 0 inteiro, 1 sumido; sem `edge`, sem a borda acesa (F55, a câmera colada). */
  setDissolve(amount: number, edge?: boolean): void;
  /** Energia local (0..1): a costura na cor da classe entre o repouso e o pico (o golpe, o destaque da escolha). */
  setEnergy(amount: number): void;
  /** Troca o item na mão (`undefined` tira); `itemId` decide a mão (o arco vai na esquerda dos avatares animados). */
  setHeld(mesh: Mesh | undefined, itemId?: string): void;
  stats(): AvatarViewStats;
  dispose(): void;
}

/** Tombo da morte do boneco procedural: 1,5 rad em 0,5 s. */
const FALL = { angle: 1.5, ms: 500 };

/** O boneco procedural de antes da F49 (reserva sem o pacote dos avatares): pose por fórmula, camisa na cor. */
export class ProceduralAvatar implements AvatarView {
  readonly root: Object3D;
  readonly heldView = "avatar" as const;
  private readonly rig: AvatarRig;
  private readonly animator = new AvatarAnimator();
  private held: Mesh | undefined;
  private lastPose = "parado";
  private dissolve = 0;

  constructor(atmosphere: AtmosphereUniforms, shirtHex: string) {
    this.rig = createAvatarModel(atmosphere, shirtHex);
    this.root = this.rig.root;
    this.root.name = AVATAR_LAYER;
  }

  update(f: AvatarFrame): void {
    const s = f.signals;
    applyPose(this.rig, this.animator.pose({ speed: s.speed, grounded: s.grounded, crouching: s.crouching, swimming: s.swimming, sliding: s.sliding, pitch: f.pitch }, f.dt));
    if (f.armSwing > 0) this.rig.rightArm.rotation.x = swingArm(f.armSwing);
    const fall = s.dead && f.diedAt >= 0 ? Math.min(1, (f.now - f.diedAt) / FALL.ms) * FALL.angle : 0;
    this.root.rotation.set(-fall, f.yaw, 0, "YXZ");
    this.lastPose = s.dead ? "morte" : s.swimming ? "nadando" : !s.grounded ? "no-ar" : s.speed > 0.15 ? "andando" : "parado";
  }

  /** Só o braço do golpe (vitrines): o ângulo de `swingArm`. */
  swing(angle: number): void {
    this.rig.rightArm.rotation.x = angle;
  }

  setDissolve(amount: number, edge = true): void {
    this.dissolve = amount;
    // Só o boneco (o item na mão some junto com a mão escondida).
    this.rig.surface.setDissolve(amount, edge);
  }

  setEnergy(amount: number): void {
    // O boneco não tem a máscara da costura: o mesmo envelope do avatar, sem efeito visível (como antes).
    const role = VISUAL_ROLES.heroi;
    this.rig.surface.setSeam(role.accent + (role.accentPeak - role.accent) * Math.max(0, Math.min(1, amount)));
  }

  setHeld(mesh: Mesh | undefined): void {
    if (this.held) this.rig.hand.remove(this.held);
    this.held = mesh;
    if (mesh) {
      poseHeld(mesh);
      this.rig.hand.add(mesh);
    }
  }

  stats(): AvatarViewStats {
    return { model: "procedural", clip: this.lastPose, lod: 1, dissolve: this.dissolve, seen: {} };
  }

  dispose(): void {
    this.rig.surface.dispose();
    this.root.removeFromParent();
  }
}

/**
 * O avatar de um jogador: o personagem animado da classe (`ClassDef.character`) com os detalhes na cor
 * `hex` (a da classe, F85), ou, sem o esqueleto ou o arquivo do personagem (F88), o boneco procedural com a
 * camisa na cor. `lod`
 * desencontra o LOD da animação entre avatares. A partida e a tela de escolha (F85) usam o mesmo.
 */
export function createAvatarView(library: AvatarLibrary | undefined, atmosphere: AtmosphereUniforms, hex: string, character: AvatarCharacter, lod = 0): AvatarView {
  return library?.has(character) ? new SkinnedAvatar(library, atmosphere, character, hex, lod) : new ProceduralAvatar(atmosphere, hex);
}
